import { View } from "../index";
import { BaseDataBind, DataBindConstructor } from "./types/base";

/**
 * Manager class that handles processing view data binds.
 * Uses Proxy to listen for changes in view.data object, which this class also initiates.
 */
export default class DataBindManager
{
    private view: View;
    private bindTypes: BaseDataBind[];

    /** Default values. */
    public defaults: {[key: string]: DataBindDefaultValue};
    public propertyWasSetInViewData: {[propertyName: string]: boolean};

    /**
     * Creates a new DataBindManager for a view.
     * @param view The view to process the data binds for.
     */
    constructor(view: View)
    {
        this.view = view;
        this.bindTypes = [];
        this.defaults = {};
        this.propertyWasSetInViewData = {};
    }

    /** Populate and initialize the view data proxy. */
    private initViewDataProxy()
    {
        let initialViewData = this.view.data !== undefined ? this.view.data : {};

        // Populate view data with defaults, so that ALL properties exist before
        // creating the Proxy. IE's polyfill for Proxy can't detect new properties!
        for(let key in this.defaults)
        {
            let defaultValue = this.defaults[key];
            initialViewData[key] = defaultValue.value;
        }
        
        // Initiate the proxy object and start listening for changes
        this.view.data = new Proxy(initialViewData, {
            set: (obj: any, name: PropertyKey, value: Object | ProxyValueWrapper) =>
            {
                let newValue: any;
                let senderElement: Element = null;

                if (value instanceof ProxyValueWrapper)
                {
                    let proxyValue = value as ProxyValueWrapper;
                    newValue = proxyValue.value;
                    senderElement = proxyValue.senderElement;
                }
                else
                {
                    newValue = value;
                }

                obj[name] = newValue;

                for(let bind of this.bindTypes)
                {
                    bind.set(name as string, newValue, senderElement);
                }

                return true
            }
        });
    }

    /** Apply default values to the binds. */
    private applyDefaultValues()
    {
        for(let propertyName in this.defaults)
        {
            let defaultValue = this.defaults[propertyName];

            this.view.data[propertyName] = new ProxyValueWrapper(defaultValue.value);
        }
    }

    /**
     * Executes all data bind types.
     */
    public run()
    {
        for(let bind of this.bindTypes)
        {
            bind.run();
        }

        this.initViewDataProxy();
        this.applyDefaultValues();
    }

    public setDefaultValue(bind: DataBindTarget, value: any)
    {
        let updateDefaultValue = false;
        if (this.propertyWasSetInViewData[bind.propertyName] === undefined)
        {
            this.propertyWasSetInViewData[bind.propertyName] = this.view.data.hasOwnProperty(bind.propertyName);
        }

        // View inititally overrides the default value
        if (this.propertyWasSetInViewData[bind.propertyName])
        {
            value = this.view.data[bind.propertyName];
        }

        if (value !== undefined)
        {
            let existing = this.defaults[bind.propertyName];

            // If we already have a default value, we don't want an empty string to replace it!
            if (existing !== undefined && value == '')
            {
                updateDefaultValue = false;
            }
            else
            {
                updateDefaultValue = true;
            }
        }

        if (updateDefaultValue)
        {
            this.defaults[bind.propertyName] = {
                element: bind.element,
                value: value
            };
        }
    }

    /**
     * Registers a new data bind type.
     * @param dataBindType Type of DataBind to add.
     */
    public add(dataBindType: DataBindConstructor)
    {
        this.bindTypes.push(new dataBindType(this, this.view));
    } 
}

/**
 * Represents a wrapper of a changed value.
 * Keeps track of the element that originated the value change event.
 */
export class ProxyValueWrapper
{
    public senderElement: Element;
    public value: any;
    
    constructor(value: any, element?: Element)
    {
        this.value = value;
        this.senderElement = element;
    }
}

/**
 * Generic data bind target.
 */
export interface DataBindTarget
{
    propertyName: string;
    element: Element;
}

/**
 * Generic default data bind value. 
 * Keeps track of the source element.
 */
export interface DataBindDefaultValue
{
    element: Element;
    value: any;
}
